import { useQueryClient } from '@tanstack/react-query'
import { type AxiosRequestConfig } from 'axios'
import { useMutator as useOrvalMutator } from '@dhl/receiver-axios'

/**
 * Basket write request URLs share this prefix, so we use it to detect when a mutation should
 * refresh the basket reads.
 */
const basketUrlPrefix = '/consumer-sender-services/basket'

const isBasketWrite = (config: AxiosRequestConfig): boolean => {
  const method = (config.method ?? 'GET').toUpperCase()
  return method !== 'GET' && !!config.url?.startsWith(basketUrlPrefix)
}

export const useMutator = <T>(): ((config: AxiosRequestConfig) => Promise<T>) => {
  const mutator = useOrvalMutator<T>()
  const queryClient = useQueryClient()

  return async (config: AxiosRequestConfig) => {
    const result = await mutator(config)

    // Centralised cache invalidation: a successful basket mutation refreshes the basket and the
    // basket summary reads. Keeping this here means individual call sites never have to remember
    // to invalidate after mutating the basket.
    if (isBasketWrite(config)) {
      queryClient.invalidateQueries({ queryKey: [basketUrlPrefix] })
      queryClient.invalidateQueries({ queryKey: [`${basketUrlPrefix}/summary`] })
    }

    return result
  }
}
